import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import TrialBanner from "../components/TrialBanner";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Activity, Clock, TrendingUp, TrendingDown, Bell, AlertCircle, RefreshCw, ChevronRight,
} from "lucide-react";

const MODULE_TITLE = {
  new_hire: "New Hire",
  annual: "Annual",
  critical_incident: "Critical Incident",
};

export default function AgencyEngagement() {
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/agency/engagement?window_days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load engagement data.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <AdminLayout>
        <TrialBanner />
        <div data-testid="engagement-error-state" className="rounded-md border p-6"
             style={{ borderColor: "hsl(var(--haruki-fire))", background: "hsl(var(--haruki-fire) / 0.06)" }}>
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5" style={{ color: "hsl(var(--haruki-fire))" }} />
            <div className="flex-1">
              <div className="font-heading text-base font-semibold">Couldn't load engagement data</div>
              <p className="mt-1 text-sm text-muted-foreground">{error}</p>
              <Button
                data-testid="engagement-retry-button"
                onClick={load}
                className="mt-4"
                size="sm"
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              >
                <RefreshCw className="mr-2 h-3 w-3" /> Retry
              </Button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading || !data) {
    return (
      <AdminLayout>
        <TrialBanner />
        <div data-testid="engagement-loading" className="text-sm text-muted-foreground">Loading engagement…</div>
      </AdminLayout>
    );
  }

  const trendUp = (data.completions_delta ?? 0) >= 0;
  const sosTrendUp = data.sos_recent > data.sos_prior;

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <TrialBanner />
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Agency Console · Engagement
            </div>
            <h1 className="font-heading text-3xl font-bold">Engagement Dashboard</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Aggregate participation activity for your agency. Individual scores and scenario content remain
              confidential — this dashboard shows participation patterns only.
            </p>
          </div>
          <div className="w-40">
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger data-testid="engagement-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Top stat row */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            test="engagement-active-users"
            icon={Activity}
            label="Active members"
            value={`${data.active_users}/${data.roster_size}`}
            subline={`${Math.round(data.active_rate * 100)}% of roster active in window`}
            tone="leaf"
          />
          <Stat
            test="engagement-completions"
            icon={trendUp ? TrendingUp : TrendingDown}
            label="Completions"
            value={data.completions_recent}
            subline={`${data.completions_delta >= 0 ? "+" : ""}${Math.round((data.completions_delta || 0) * 100)}% vs. prior period`}
            tone={trendUp ? "leaf" : "rust"}
          />
          <Stat
            test="engagement-ttf"
            icon={Clock}
            label="Avg. days to first scenario"
            value={data.avg_days_to_first_scenario ?? "—"}
            subline="Lower is better — onboarding friction proxy"
            tone="teal"
          />
          <Stat
            test="engagement-sos"
            icon={Bell}
            label="SOS signals"
            value={data.sos_recent}
            subline={`${data.sos_prior} in prior period (${sosTrendUp ? "↑ rising" : "→ stable / down"})`}
            tone={data.sos_recent > 0 ? "rust" : "neutral"}
          />
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {/* Daily completions chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-base">Daily completions</CardTitle>
            </CardHeader>
            <CardContent>
              <DailyBars series={data.daily_completions} />
              <p className="mt-3 text-xs text-muted-foreground">
                Days with zero completions are common during high-load operational periods — and a useful signal
                to coordinate command-pushed scenarios.
              </p>
            </CardContent>
          </Card>

          {/* Hour-of-day heatmap */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">When do they train?</CardTitle>
            </CardHeader>
            <CardContent>
              <HourHeatmap data={data.hour_heatmap} />
              <p className="mt-3 text-xs text-muted-foreground">
                Hour-of-day distribution helps with critical-incident push timing.
              </p>
            </CardContent>
          </Card>

          {/* Module completion */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="font-heading text-base">Module completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                {Object.entries(data.module_stats || {}).map(([k, v]) => (
                  <ModuleStat key={k} type={k} stat={v} onDrillDown={() => nav(`/agency/engagement/module/${k}`)} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Confidentiality reminder */}
        <div className="mt-10 flex items-start gap-3 rounded-md border border-border p-4 text-xs text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Everything on this page is aggregate participation data. Individual scenario content, individual
            scores, and SOS session content are <strong>never</strong> visible to admins — by design and by database policy.
            Aggregate HMDE distribution unlocks once your cohort reaches {data.min_cohort_required} members.
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// ---- Subcomponents ----
const TONE = {
  leaf: "hsl(var(--haruki-leaf))",
  teal: "hsl(var(--haruki-teal))",
  rust: "hsl(var(--haruki-rust))",
  neutral: "hsl(var(--haruki-neutral))",
};

const Stat = ({ icon: Icon, label, value, subline, tone = "leaf", test }) => (
  <Card data-testid={test}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className="rounded-md p-1.5" style={{ background: `${TONE[tone]} / 0.1`, backgroundColor: TONE[tone], color: "white" }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{subline}</div>
    </CardContent>
  </Card>
);

const DailyBars = ({ series = [] }) => {
  const max = Math.max(1, ...series.map((s) => s.completions));
  return (
    <div className="flex h-40 items-end gap-1">
      {series.map((s) => {
        const pct = Math.round((s.completions / max) * 100);
        return (
          <div key={s.date} className="group flex flex-1 flex-col items-center">
            <div
              className="w-full rounded-t transition-opacity hover:opacity-80"
              style={{
                height: `${Math.max(pct, 4)}%`,
                background: s.completions > 0 ? "hsl(var(--haruki-leaf))" : "hsl(var(--border))",
              }}
              title={`${s.date}: ${s.completions}`}
            />
          </div>
        );
      })}
    </div>
  );
};

const HourHeatmap = ({ data = [] }) => {
  const max = Math.max(1, ...data);
  return (
    <div className="grid grid-cols-12 gap-1">
      {data.map((v, h) => {
        const alpha = v === 0 ? 0.08 : 0.15 + (v / max) * 0.85;
        return (
          <div
            key={h}
            className="aspect-square rounded"
            style={{ background: `hsl(var(--haruki-leaf) / ${alpha})` }}
            title={`${h}:00 — ${v}`}
          />
        );
      })}
    </div>
  );
};

const ModuleStat = ({ type, stat, onDrillDown }) => {
  const rate = Math.round((stat.completion_rate || 0) * 100);
  const pending = (stat.enrolled || 0) - (stat.completed || 0);
  return (
    <button
      type="button"
      data-testid={`engagement-module-${type}`}
      onClick={onDrillDown}
      className="group rounded-md border border-border p-4 text-left transition-all hover:border-foreground/30 hover:shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {MODULE_TITLE[type] || type}
        </div>
        <div className="font-heading text-2xl font-extrabold tabular-nums" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
          {rate}%
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "hsl(var(--secondary))" }}>
        <div className="h-full rounded-full" style={{ width: `${rate}%`, background: "hsl(var(--haruki-leaf))" }} />
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{stat.completed} of {stat.enrolled || stat.denominator || 0} {type === "critical_incident" ? "pushed" : "enrolled"}</span>
        <span className="inline-flex items-center gap-1 font-medium text-foreground/60 transition-colors group-hover:text-foreground">
          {pending > 0 ? `${pending} pending` : "Drill in"} <ChevronRight className="h-3 w-3" />
        </span>
      </div>
    </button>
  );
};
